import type { MetricDto, Unit } from '@crm/shared/contracts';

/**
 * Presentation helpers of the home page, kept out of the components so they can be tested
 * (AGENTS.md: no business logic in React components). They shape figures the API computed; they
 * never compute a KPI.
 */

/**
 * Sequential scale for the heatmap: one hue, eight steps from weak to strong
 * (docs/DASHBOARD_SPECIFICATION.md §1.1). The values live in index.css (`--heat-0` … `--heat-7`)
 * so the dark theme can re-tune them.
 */
export const HEAT_STEPS = [
  'var(--heat-0)',
  'var(--heat-1)',
  'var(--heat-2)',
  'var(--heat-3)',
  'var(--heat-4)',
  'var(--heat-5)',
  'var(--heat-6)',
  'var(--heat-7)',
] as const;

/** Colour of a value on the scale from 0 to `max`; `null` for a cell without a value. */
export function heatColor(value: number | null, max: number): string | null {
  if (value === null) return null;
  if (max <= 0 || value <= 0) return HEAT_STEPS[0];
  const index = Math.min(HEAT_STEPS.length - 1, Math.ceil((value / max) * (HEAT_STEPS.length - 1)));
  return HEAT_STEPS[index] ?? HEAT_STEPS[0];
}

export interface Cell {
  readonly weekday: number;
  readonly hour: number;
  readonly value: number | null;
}

/** The `count` cells with the highest value (ties: earlier in the week, then earlier hour). */
export function topCells<T extends Cell>(cells: readonly T[], count: number): T[] {
  return cells
    .filter((c) => c.value !== null && c.value > 0)
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0) || a.weekday - b.weekday || a.hour - b.hour)
    .slice(0, count);
}

/**
 * The comparison of a figure the API returns as a current/previous pair of totals (ads). Mirrors
 * the server's metric shape so the same comparison badge can show it.
 */
export function pairMetric(
  value: number | null,
  previous: number | null,
  unit: Unit,
  kind: 'count' | 'rate' = 'count',
): MetricDto {
  const change = value === null || previous === null ? null : value - previous;
  const changeRatio =
    kind === 'rate' || change === null || previous === null || previous === 0
      ? null
      : change / previous;
  return { value, previous, change, changeRatio, unit };
}

/** Share of a part in a whole; `null` for an empty whole (never 0 %). */
export const share = (part: number, whole: number): number | null =>
  whole === 0 ? null : part / whole;

/** Money in minor units → major units (for comparisons shown in whole currency units). */
export const major = (minor: number | null): number | null => (minor === null ? null : minor / 100);
